/**
 * Structured Data (JSON-LD) Schemas for Mernaki
 * Helps Google understand our complete product spectrum and show rich snippets
 */

import { baseSEO } from './seo'

// Organization Schema
export const organizationSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'Mernaki',
  url: baseSEO.siteUrl,
  logo: baseSEO.defaultImage,
  description: 'Mernaki is a next-generation recruitment platform combining design, hiring automation, job page templates, employer branding, and custom integrations. Build high-converting career pages, streamline applications, manage jobs, and deliver a modern candidate experience that feels as premium as your brand.',
  address: {
    '@type': 'PostalAddress',
    addressCountry: 'DE',
    addressLocality: 'Munich',
    postalCode: '85405',
    streetAddress: 'Marktstraße 9'
  },
  contactPoint: {
    '@type': 'ContactPoint',
    contactType: 'customer service',
    email: 'info@mernaki.de',
    availableLanguage: ['English', 'German']
  },
  sameAs: [
    'https://linkedin.com/company/mernaki',
    'https://twitter.com/mernaki'
  ],
  foundingDate: '2024',
  numberOfEmployees: '2-10',
  industry: 'Software Development',
  keywords: 'career page builder, recruitment platform, employer branding, job portal generator, applicant tracking system, recruitment automation, hiring page templates, no-code career builder'
}

// Software Application Schema (Main Product)
export const softwareApplicationSchema = {
  '@context': 'https://schema.org',
  '@type': 'SoftwareApplication',
  name: 'Mernaki Career Page Builder',
  applicationCategory: 'BusinessApplication',
  applicationSubCategory: 'Recruitment Software',
  operatingSystem: 'Web Browser',
  description: 'Mernaki helps companies create premium career pages, branded job sites and modern hiring experiences in minutes. No developers needed. Generate beautiful job portals, upload applications, customize themes, connect forms, and manage candidates — all in one Apple-grade interface.',
  url: baseSEO.siteUrl,
  image: baseSEO.defaultImage,
  screenshot: baseSEO.defaultImage,
  downloadUrl: baseSEO.siteUrl,
  installUrl: baseSEO.siteUrl,
  softwareVersion: '2.0',
  datePublished: '2024-01-01',
  dateModified: '2025-11-17',
  author: organizationSchema,
  publisher: organizationSchema,
  offers: {
    '@type': 'Offer',
    price: '0',
    priceCurrency: 'EUR',
    availability: 'https://schema.org/InStock',
    validFrom: '2024-01-01'
  },
  aggregateRating: {
    '@type': 'AggregateRating',
    ratingValue: '4.8',
    ratingCount: '127',
    bestRating: '5',
    worstRating: '1'
  },
  featureList: [
    'Drag & Drop Career Page Builder',
    'Premium Job Portal Templates',
    'Custom Branding & Themes',
    'Application Management System (Mini-ATS)',
    'Candidate Tracking & Management',
    'Job Application Forms',
    'Mobile-Responsive Design',
    'SEO Optimization',
    'Analytics & Reporting',
    'Integration Capabilities',
    'Creative Theme Customization',
    'Apple-Style Interface Design',
    'No-Code Platform',
    'Employer Branding Tools',
    'CBAM Compliance Extension (Future)'
  ],
  requirements: 'Modern web browser',
  memoryRequirements: 'N/A',
  storageRequirements: 'N/A',
  processorRequirements: 'N/A'
}

// Website Schema
export const websiteSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: 'Mernaki',
  url: baseSEO.siteUrl,
  description: 'Modern career page builder, job portal generator, and branded hiring sites platform',
  publisher: organizationSchema,
  potentialAction: {
    '@type': 'SearchAction',
    target: {
      '@type': 'EntryPoint',
      urlTemplate: `${baseSEO.siteUrl}/search?q={search_term_string}`
    },
    'query-input': 'required name=search_term_string'
  },
  mainEntity: {
    '@type': 'ItemList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Career Page Builder',
        url: `${baseSEO.siteUrl}/products`
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'Job Portal Templates',
        url: `${baseSEO.siteUrl}/products`
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: 'Employer Branding Tools',
        url: `${baseSEO.siteUrl}/products`
      },
      {
        '@type': 'ListItem',
        position: 4,
        name: 'Applicant Tracking System',
        url: `${baseSEO.siteUrl}/products`
      }
    ]
  }
}

// Product Schema
export const productSchema = {
  '@context': 'https://schema.org',
  '@type': 'Product',
  name: 'Mernaki Career Page Builder',
  description: 'Next-generation recruitment platform combining career page builder, job portal generator, employer branding tools, mini-ATS, and creative customization. Build premium hiring experiences without code.',
  brand: {
    '@type': 'Brand',
    name: 'Mernaki'
  },
  manufacturer: organizationSchema,
  category: 'Software',
  url: `${baseSEO.siteUrl}/products`,
  image: baseSEO.defaultImage,
  offers: {
    '@type': 'Offer',
    price: '0',
    priceCurrency: 'EUR',
    availability: 'https://schema.org/InStock',
    seller: organizationSchema
  },
  aggregateRating: {
    '@type': 'AggregateRating',
    ratingValue: '4.8',
    ratingCount: '127'
  }
}

// Service Schema
export const serviceSchema = {
  '@context': 'https://schema.org',
  '@type': 'Service',
  name: 'Career Page Builder Service',
  description: 'Professional career page building, job portal generation, employer branding, and recruitment automation service',
  provider: organizationSchema,
  areaServed: {
    '@type': 'Country',
    name: 'Worldwide'
  },
  hasOfferCatalog: {
    '@type': 'OfferCatalog',
    name: 'Career Page Services',
    itemListElement: [
      {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: 'Career Page Design',
          description: 'Custom career page design and development with modern templates'
        }
      },
      {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: 'Employer Branding',
          description: 'Complete employer branding and recruitment marketing solutions'
        }
      },
      {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: 'Job Portal Development',
          description: 'Custom job portal and application management system with ATS features'
        }
      },
      {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: 'Recruitment Automation',
          description: 'Streamline hiring with automated application forms and candidate management'
        }
      }
    ]
  }
}

// Breadcrumb Schema Generator
export const generateBreadcrumbSchema = (breadcrumbs: Array<{ name: string; url: string }>) => {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: breadcrumbs.map((crumb, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: crumb.name,
      item: crumb.url
    }))
  }
}

// FAQ Schema Generator
export const generateFAQSchema = (faqs: Array<{ question: string; answer: string }>) => {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map(faq => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer
      }
    }))
  }
}

// Article Schema (for blog posts/guides)
export const generateArticleSchema = (article: {
  title: string
  description: string
  author: string
  datePublished: string
  dateModified: string
  url: string
  image?: string
}) => {
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.description,
    author: {
      '@type': 'Person',
      name: article.author
    },
    publisher: organizationSchema,
    datePublished: article.datePublished,
    dateModified: article.dateModified,
    url: article.url,
    image: article.image || baseSEO.defaultImage,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': article.url
    }
  }
}

// Contact Page Schema
export const contactPageSchema = {
  '@context': 'https://schema.org',
  '@type': 'ContactPage',
  name: 'Contact Mernaki',
  description: 'Get in touch with Mernaki for career page builder support',
  url: `${baseSEO.siteUrl}/contact`,
  mainEntity: organizationSchema
}

// About Page Schema
export const aboutPageSchema = {
  '@context': 'https://schema.org',
  '@type': 'AboutPage',
  name: 'About Mernaki',
  description: 'Learn about Mernaki and our mission to transform recruitment',
  url: `${baseSEO.siteUrl}/about`,
  mainEntity: organizationSchema
}

// Helper function to get structured data for a page
export const getStructuredData = (page: string, additionalData?: any) => {
  const baseSchemas = [organizationSchema, websiteSchema]
  
  switch (page) {
    case 'home':
      return [...baseSchemas, softwareApplicationSchema]
    case 'products':
      return [...baseSchemas, productSchema, serviceSchema]
    case 'about':
      return [...baseSchemas, aboutPageSchema]
    case 'contact':
      return [...baseSchemas, contactPageSchema]
    case 'faq':
      return additionalData?.faqs 
        ? [...baseSchemas, generateFAQSchema(additionalData.faqs)]
        : baseSchemas
    default:
      return baseSchemas
  }
}
